import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Menu, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { AuthContext } from '../../context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import './SideMenu.css';

const SideMenu = ({
  menuItems = [],
  currentPath = '',
  onNavigate,
  isExpanded = false,
  onToggleExpand
}) => {
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  const currentBasePath = currentPath.split('?')[0];
  const isActive = (item) => {
    if (item.path === '/dashboard') return currentBasePath === '/dashboard';
    return currentBasePath === item.path || currentBasePath.startsWith(`${item.path}/`);
  };

  const go = (item) => {
    if (!item.disabled && item.path && onNavigate) onNavigate(item.path);
  };

  const confirmLogout = () => {
    logout();
    setShowLogoutDialog(false);
    navigate('/login');
  };

  const renderItem = (item, mobile = false) => {
    const Icon = item.icon;
    return (
      <button
        key={item.id}
        type="button"
        onClick={() => go(item)}
        className={`ks-nav-item ${isActive(item) ? 'ks-nav-item--active' : ''} ${mobile ? 'ks-nav-item--mobile' : ''}`}
        title={!mobile && !isExpanded ? item.label : undefined}
        aria-current={isActive(item) ? 'page' : undefined}
      >
        {Icon && <Icon size={18} strokeWidth={1.8} />}
        {(isExpanded || mobile) && <span>{item.label}</span>}
      </button>
    );
  };

  return (
    <>
      <aside className={`ks-sidebar ${isExpanded ? 'ks-sidebar--expanded' : ''}`}>
        <div className="ks-sidebar-brand">
          <img src="/logo.png" alt="KeyShop" className="ks-sidebar-logo" />
          {isExpanded && (
            <div className="ks-brand-copy">
              <strong>KeyShop</strong>
              <span>Marketplace Admin</span>
            </div>
          )}
        </div>

        <button
          type="button"
          className="ks-sidebar-toggle"
          onClick={() => onToggleExpand?.(!isExpanded)}
          aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
          title={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
        >
          {isExpanded ? <PanelLeftClose size={15} /> : <PanelLeftOpen size={15} />}
        </button>

        <nav className="ks-sidebar-nav" aria-label="Admin navigation">
          {menuItems.map((item) => renderItem(item))}
        </nav>

        <div className="ks-sidebar-footer">
          <button
            type="button"
            className="ks-logout-button"
            onClick={() => setShowLogoutDialog(true)}
            title={!isExpanded ? 'Logout' : undefined}
          >
            <LogOut size={18} strokeWidth={1.8} />
            {isExpanded && <span>Logout</span>}
          </button>
          {isExpanded && <small>v1.0 · © KeyShop</small>}
        </div>
      </aside>

      <div className="ks-mobile-nav">
        <div className="ks-mobile-brand-row">
          <div className="ks-mobile-brand">
            <img src="/logo.png" alt="KeyShop" />
            <div><strong>KeyShop</strong><span>Marketplace Admin</span></div>
          </div>
          <button type="button" onClick={() => setShowLogoutDialog(true)} aria-label="Logout">
            <LogOut size={19} />
          </button>
        </div>
        <div className="ks-mobile-nav-scroll">
          <Menu size={18} className="ks-mobile-menu-icon" />
          {menuItems.map((item) => renderItem(item, true))}
        </div>
      </div>

      <Dialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <DialogContent showCloseButton={false} className="ks-logout-dialog">
          <DialogHeader>
            <DialogTitle>Log out?</DialogTitle>
            <DialogDescription>
              You'll need to sign in again to access the KeyShop admin panel.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="ks-logout-actions">
            <Button variant="outline" onClick={() => setShowLogoutDialog(false)}>Stay signed in</Button>
            <Button variant="destructive" onClick={confirmLogout}>
              <LogOut size={16} /> Log out
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SideMenu;
